import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

interface User {
  id: number;
  email?: string;
  name: string;
  role: string;
  company_id?: number;
  company_id_string?: string;
}

interface AuthContextType {
  user: User | null;
  login: (identifier: string, password: string, type?: 'worker' | 'company' | 'super_admin') => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkStoredAuth();
  }, []);

  const checkStoredAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const userData = await authService.verifyToken();
        setUser(userData);
      }
    } catch (error) {
      console.error('Error checking stored auth:', error);
      localStorage.removeItem('token');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (identifier: string, password: string, type: 'worker' | 'company' | 'super_admin' = 'worker') => {
    try {
      let response;
      
      if (type === 'company') {
        // Login para empresas
        response = await authService.loginCompany(identifier, password);
      } else if (type === 'super_admin') {
        // Login para super admin
        response = await authService.loginSuperAdmin(identifier, password);
      } else {
        // Login para trabajadores
        response = await authService.login(identifier, password);
      }

      const { token, user: userData } = response;
      
      localStorage.setItem('token', token);
      setUser(userData);
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.message || 'Error al iniciar sesión');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
